import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "../components/ui/dialog";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "../components/ui/table";
import { Plus, Database, Pencil, Trash } from "@phosphor-icons/react";
import { useCollectionSchema, useReferenceData } from "../hooks/useCollectionSchema";
import DynamicForm from "../components/DynamicForm";

const DynamicCollectionPage = () => {
    const { collectionName } = useParams();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingItem, setEditingItem] = useState(null);
    const [formData, setFormData] = useState({});

    // Get dynamic schema
    const { schema, fields, displayName, loading: schemaLoading } = useCollectionSchema(collectionName);
    const { references } = useReferenceData(fields);

    useEffect(() => {
        if (collectionName) {
            fetchItems();
        }
    }, [collectionName]);

    // Initialize form data when fields change
    useEffect(() => {
        if (fields.length > 0 && !editingItem) {
            initializeFormData();
        }
    }, [fields]);

    const initializeFormData = () => {
        const initial = {};
        fields.forEach(field => {
            initial[field.name] = field.default ?? (field.type === "number" ? 0 : "");
        });
        setFormData(initial);
    };

    const fetchItems = async () => {
        try {
            setLoading(true);
            // Try collection-specific endpoint first, fallback to admin endpoint
            let response;
            try {
                response = await axios.get(`${API}/${collectionName}`);
            } catch (e) {
                // Fallback to admin collections data endpoint
                response = await axios.get(`${API}/admin/collections/${collectionName}/data`);
                response.data = response.data.data || [];
            }
            setItems(response.data);
        } catch (error) {
            console.error("Error fetching items:", error);
            setItems([]);
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        // Validate required fields
        const missingRequired = fields
            .filter(f => f.required && (formData[f.name] === "" || formData[f.name] === undefined))
            .map(f => f.label || f.name);
        
        if (missingRequired.length > 0) {
            toast.error(`Campos requeridos: ${missingRequired.join(", ")}`);
            return;
        }

        try {
            if (editingItem) {
                // Try specific endpoint first, fallback to admin
                try {
                    await axios.put(`${API}/${collectionName}/${editingItem.id}`, formData);
                } catch (e) {
                    await axios.put(`${API}/admin/collections/${collectionName}/data/${editingItem.id}`, { data: formData });
                }
                toast.success("Registro actualizado");
            } else {
                try {
                    await axios.post(`${API}/${collectionName}`, formData);
                } catch (e) {
                    await axios.post(`${API}/admin/collections/${collectionName}/data`, { data: formData });
                }
                toast.success("Registro creado");
            }
            fetchItems();
            setDialogOpen(false);
            resetForm();
        } catch (error) {
            toast.error(error.response?.data?.detail || "Error al guardar");
        }
    };

    const handleEdit = (item) => {
        setEditingItem(item);
        const data = {};
        fields.forEach(field => {
            data[field.name] = item[field.name] ?? field.default ?? (field.type === "number" ? 0 : "");
        });
        setFormData(data);
        setDialogOpen(true);
    };

    const handleDelete = async (id) => {
        if (!window.confirm("¿Eliminar este registro?")) return;
        try {
            try {
                await axios.delete(`${API}/${collectionName}/${id}`);
            } catch (e) {
                await axios.delete(`${API}/admin/collections/${collectionName}/data/${id}`);
            }
            toast.success("Registro eliminado");
            fetchItems();
        } catch (error) {
            toast.error("Error al eliminar");
        }
    };

    const resetForm = () => {
        setEditingItem(null);
        initializeFormData();
    };

    // Get display value for a field
    const getDisplayValue = (field, value) => {
        if (value === undefined || value === null || value === "") return "-";
        
        switch (field.type) {
            case "reference":
                const refData = references[field.ref_collection] || [];
                const refItem = refData.find(r => r.id === value);
                return refItem?.nombre || refItem?.name || refItem?.title || refItem?.plate || value;
            case "boolean":
                return value ? "Sí" : "No";
            case "date":
                return new Date(value).toLocaleDateString("es-ES");
            case "number":
                if (field.name.includes("budget") || field.name.includes("price") || field.name.includes("presupuesto")) {
                    return `${value.toLocaleString("es-ES")} €`;
                }
                return value;
            case "select":
                return value.charAt(0).toUpperCase() + value.slice(1).replace("-", " ");
            default:
                return String(value);
        }
    };

    // Get status badge if field is status-like
    const isStatusField = (fieldName) => {
        return fieldName === "status" || fieldName === "estado";
    };

    const getStatusBadge = (value) => {
        if (!value) return "-";
        const statusMap = {
            "disponible": "badge-success",
            "activo": "badge-success",
            "completado": "badge-success",
            "completada": "badge-success",
            "en-uso": "badge-warning",
            "en-progreso": "badge-warning",
            "pausado": "badge-warning",
            "pendiente": "badge-info",
            "mantenimiento": "badge-danger",
            "alta": "badge-danger",
            "media": "badge-warning",
            "baja": "badge-info",
        };
        const label = value.charAt(0).toUpperCase() + value.slice(1).replace("-", " ");
        return <span className={statusMap[value] || "badge-info"}>{label}</span>;
    };

    if (loading || schemaLoading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="spinner"></div>
            </div>
        );
    }

    if (!schema) {
        return (
            <div className="flex flex-col items-center justify-center h-64">
                <Database size={48} className="text-[#CCCCCC] mb-4" />
                <p className="text-[#555555]">Colección no encontrada</p>
            </div>
        );
    }

    // Get table columns from schema (exclude text fields and arrays)
    const tableFields = fields.filter(f => f.type !== "text" && f.type !== "array");

    return (
        <div className="space-y-6 animate-in" data-testid={`${collectionName}-page`}>
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                    <h1 className="font-heading font-bold text-2xl md:text-3xl">{displayName}</h1>
                    <p className="text-[#555555] mt-1">
                        {items.length} registros
                    </p>
                </div>
                <Dialog open={dialogOpen} onOpenChange={(open) => {
                    setDialogOpen(open);
                    if (!open) resetForm();
                }}>
                    <DialogTrigger asChild>
                        <Button className="btn-primary" data-testid={`add-${collectionName}-btn`}>
                            <Plus size={20} className="mr-2" />
                            Añadir
                        </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
                        <DialogHeader>
                            <DialogTitle className="font-heading">
                                {editingItem ? `Editar ${displayName}` : `Nuevo ${displayName}`}
                            </DialogTitle>
                        </DialogHeader>
                        <form onSubmit={handleSubmit} className="mt-4">
                            <DynamicForm 
                                fields={fields}
                                formData={formData}
                                setFormData={setFormData}
                                references={references}
                            />
                            <div className="flex gap-3 pt-6">
                                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="flex-1">
                                    Cancelar
                                </Button>
                                <Button type="submit" className="btn-primary flex-1" data-testid={`${collectionName}-save-btn`}>
                                    {editingItem ? "Guardar cambios" : "Añadir"}
                                </Button>
                            </div>
                        </form>
                    </DialogContent>
                </Dialog>
            </div>

            {/* Dynamic Table */}
            <div className="bg-white border border-[#E5E5E5] rounded-sm overflow-hidden">
                <div className="overflow-x-auto">
                    <Table>
                        <TableHeader>
                            <TableRow className="table-industrial">
                                {tableFields.map(field => (
                                    <TableHead key={field.name}>
                                        {field.label || field.name}
                                    </TableHead>
                                ))}
                                <TableHead className="text-right">Acciones</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {items.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={tableFields.length + 1} className="text-center py-12">
                                        <Database size={48} className="mx-auto mb-4 text-[#CCCCCC]" />
                                        <p className="text-[#555555]">No hay registros</p>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                items.map((item) => (
                                    <TableRow key={item.id} className="table-industrial" data-testid={`${collectionName}-row-${item.id}`}>
                                        {tableFields.map(field => (
                                            <TableCell key={field.name}>
                                                {isStatusField(field.name) ? (
                                                    getStatusBadge(item[field.name])
                                                ) : (
                                                    getDisplayValue(field, item[field.name])
                                                )}
                                            </TableCell>
                                        ))}
                                        <TableCell>
                                            <div className="flex items-center justify-end gap-2">
                                                <button
                                                    onClick={() => handleEdit(item)}
                                                    className="p-2 hover:bg-[#F8F9FA] rounded-sm"
                                                    data-testid={`edit-${item.id}`}
                                                >
                                                    <Pencil size={18} />
                                                </button>
                                                <button
                                                    onClick={() => handleDelete(item.id)}
                                                    className="p-2 hover:bg-red-50 text-red-600 rounded-sm"
                                                    data-testid={`delete-${item.id}`}
                                                >
                                                    <Trash size={18} />
                                                </button>
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </div>
            </div>
        </div>
    );
};

export default DynamicCollectionPage;
